/**
 * Memorial Keepsakes / Memorial Photo Link V1 — preview and positioning only.
 * No checkout, orders, shipping, or backend migrations in this pass.
 *
 * Future order model (deferred):
 * keepsake_id, user_id, memorial_id, person_id, product_type, size,
 * print_image_url, qr_short_code, nfc_short_code,
 * status: draft | requested | paid | in_production | shipped,
 * shipping_name, shipping_address, created_at, updated_at
 *
 * Future digital experience fields (deferred):
 * featured_memory_ids, rotate_memories_enabled, remembrance_dates,
 * frame_mode_default_section, nfc_private_family_enabled
 */

/** V1 hero product name */
export const MEMORIAL_PHOTO_LINK_NAME = 'Memorial Photo Link';

export const MEMORIAL_PHOTO_LINK_TAGLINE =
  'A framed memorial photo that opens a living tribute by tap or scan.';

export const MEMORIAL_PHOTO_LINK_SUPPORTING =
  'Use NFC or QR to connect a physical photo in the home to a published Memorial Bridge page.';

/** Overall section label */
export const MEMORIAL_KEEPSAKES_SECTION_TITLE = 'Memorial Keepsakes';

export const MEMORIAL_KEEPSAKES_SUBTITLE =
  'Physical Memorial Photo Link keepsakes that open this memorial when scanned or tapped.';

export const MEMORIAL_KEEPSAKES_HUB_COPY =
  'Memorial Photo Link — a framed photo or tribute print that opens a published memorial by QR or NFC.';

export const MEMORIAL_KEEPSAKES_NFC_QR_COPY =
  'NFC is a tap-to-open link hidden inside the keepsake. QR is a scan-to-open code. Both use the same MyLegacyBridge dynamic link at /q/:shortCode — not the raw memorial slug.';

export const MEMORIAL_KEEPSAKES_DYNAMIC_LINK_NOTE =
  'If the memorial slug changes, the keepsake still works. If unpublished, visitors see a safe unavailable page. MyLegacyBridge can update the redirect later.';

export const MEMORIAL_KEEPSAKES_PUBLISH_REQUIRED =
  'Publish the memorial page before creating a live QR/NFC keepsake.';

/** Softer owner-facing guidance — design before activate */
export const MEMORIAL_PHOTO_LINK_PREVIEW_GUIDANCE =
  'You can design your keepsake now. Publish the memorial when you are ready to activate the QR/NFC link.';

export const MEMORIAL_PHOTO_LINK_ACTIVATION_NOTE =
  'Live QR/NFC activates after publishing';

export const MEMORIAL_PHOTO_LINK_BUILDER_NOTE =
  'Checkout and delivery will be added later. For now, this preview helps you prepare the photo, frame style and memorial link.';

export const MEMORIAL_PHOTO_LINK_REQUEST_TITLE = 'Review keepsake request';

export const MEMORIAL_PHOTO_LINK_REQUEST_NOTE =
  'Ordering is not live yet. Review your keepsake and save your request — payment and delivery will be added later.';

export const MEMORIAL_PHOTO_LINK_CHECKOUT_UNPUBLISHED =
  'You can save this keepsake draft now. Publish the memorial when you are ready to activate the QR/NFC link.';

export const MEMORIAL_PHOTO_LINK_CHECKOUT_PUBLISHED =
  'This keepsake will use the live MyLegacyBridge dynamic link.';

export const MEMORIAL_PHOTO_LINK_PRICE_PLACEHOLDER = 'Price to be confirmed';

export const MEMORIAL_PHOTO_LINK_ORDERING_PAID_ONLY =
  'Memorial Photo Link ordering is available with a paid archive plan.';

export const MEMORIAL_PHOTO_LINK_SAFE_AREA =
  'Keep the face inside the safe area.';

export const MEMORIAL_PHOTO_ENHANCE_SAFE_NOTE =
  'Edits are preview adjustments for your keepsake. The original photo is kept safe.';

export const MEMORIAL_PHOTO_ENHANCE_BEST_PHOTO_NOTE =
  'For best results, use the clearest photo available.';

export const MEMORIAL_PHOTO_ENHANCE_ADVANCED_NOTE =
  'Coming later: colour restoration, scratch repair and professional photo enhancement. Advanced restoration and colour repair may be added later.';

export const MEMORIAL_PHOTO_LINK_REQUEST_SAVED =
  'Keepsake request saved. We will be in touch about the next step.';

export const MEMORIAL_PHOTO_LINK_UPLOAD_SAVE_BLOCKED =
  'Please finish uploading your keepsake image before saving, or use the memorial portrait or choose image later.';

export const MEMORIAL_PHOTO_LINK_UPLOAD_SUCCESS =
  'Keepsake image uploaded. You can now save your request.';

export const MEMORIAL_PHOTO_LINK_UPLOAD_FAILED =
  'Could not upload keepsake image. Please try a JPG, PNG or WebP under 30 MB.';

export const MEMORIAL_KEEPSAKES_PREVIEW_DISCLAIMER =
  'Design your keepsake in Preview Mode anytime. Live QR/NFC links activate only after the memorial is published.';

export const MEMORIAL_KEEPSAKES_NFC_NOTE =
  'NFC tag links to the same secure dynamic memorial link as the QR code.';

export const MEMORIAL_KEEPSAKES_PAID_BADGE = 'Paid member feature';

export const MEMORIAL_KEEPSAKES_FREE_NOTE = 'Available with a paid archive plan.';

export const MEMORIAL_KEEPSAKES_CEMETERY_NOTE =
  'Please check cemetery, crematorium, churchyard, or local rules before attaching a plaque or QR code to a grave, urn, bench, or public memorial.';

export const MEMORIAL_KEEPSAKES_PLANNED_LABEL = 'Planned digital experience';

export const MEMORIAL_KEEPSAKES_MEMORY_OF_DAY =
  'Show a different approved memory, photo or story when the frame is tapped.';

export const MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_TITLE = 'Public QR + Private Family NFC';

export const MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_COPY =
  'The printed QR can open the public memorial tribute, while NFC can later support private family access for invited relatives.';

export const MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_PRIVACY =
  'Private family memories should only open for invited, signed-in relatives.';

export const MEMORIAL_KEEPSAKES_ANNIVERSARY_TITLE = 'Anniversary remembrance';

export const MEMORIAL_KEEPSAKES_ANNIVERSARY_COPY =
  'On birthdays, anniversaries and remembrance dates, the memorial can show a candle, selected photo, favourite song or message.';

export const MEMORIAL_KEEPSAKES_FRAME_MODE_TITLE = 'Frame Mode';

export const MEMORIAL_KEEPSAKES_FRAME_MODE_COPY =
  'A quieter memorial view designed for keepsakes: portrait, candle, voice memory, favourite song and tribute options.';

export const MEMORIAL_KEEPSAKES_FRAME_MODE_FUTURE =
  'Future link format: /q/:shortCode?mode=frame — preview only for now.';

export const MEMORIAL_KEEPSAKES_CERTIFICATE_TITLE = 'Legacy Link Certificate';

export const MEMORIAL_KEEPSAKES_CERTIFICATE_TAGLINE =
  'This keepsake opens a living memorial by tap or scan.';

export const MEMORIAL_KEEPSAKES_CERTIFICATE_FOOTER = 'Preserved with MyLegacyBridge';

export const MEMORIAL_KEEPSAKES_VOICE_EMPTY =
  'Add a voice memory to make this keepsake open with their voice.';

/** Digital experience preview rows (V1 positioning) */
export const MEMORIAL_KEEPSAKES_DIGITAL_EXPERIENCE = [
  {
    id: 'memory_of_day',
    title: 'Memory of the Day',
    copy: 'Each tap can surface a selected memory, photo or story from their life.',
  },
  {
    id: 'voice',
    title: 'Listen to their voice',
    copy: 'Open a featured voice memory so family can hear them again.',
  },
  {
    id: 'anniversary',
    title: 'Anniversary remembrance',
    copy: 'On birthdays and remembrance dates, the memorial can show a candle, message or selected memory.',
  },
  {
    id: 'tribute',
    title: 'Leave a tribute',
    copy: 'Visitors can light a candle, leave a flower, send love or write a tribute.',
  },
];

/** Main fulfilment sizes — nothing larger than A4 */
export const MEMORIAL_KEEPSAKE_MAIN_PRODUCTS = [
  {
    id: 'a6_memory_photo',
    productType: 'a6_memory_photo',
    variantOf: MEMORIAL_PHOTO_LINK_NAME,
    title: 'A6 Memory Photo',
    size: 'Approx 10 × 15 cm',
    sizeDetail: 'Small keepsake / gift',
    copy: 'A compact framed photo with hidden NFC and a memorial QR link.',
    recommended: false,
    largest: false,
    isMainFulfilment: true,
    frameClassPortrait: 'aspect-[105/148]',
    frameClassLandscape: 'aspect-[148/105]',
  },
  {
    id: 'a5_memorial_portrait',
    productType: 'a5_memorial_portrait',
    variantOf: MEMORIAL_PHOTO_LINK_NAME,
    title: 'A5 Memorial Photo Link',
    size: 'Approx 14.8 × 21 cm',
    sizeDetail: 'Recommended / main product',
    copy: 'Our recommended keepsake — a premium portrait print for the home, connected to the memorial page.',
    recommended: true,
    largest: false,
    isMainFulfilment: true,
    frameClassPortrait: 'aspect-[148/210]',
    frameClassLandscape: 'aspect-[210/148]',
  },
  {
    id: 'a4_memorial_portrait',
    productType: 'a4_memorial_portrait',
    variantOf: MEMORIAL_PHOTO_LINK_NAME,
    title: 'A4 Memorial Photo Link',
    size: 'Approx 21 × 29.7 cm',
    sizeDetail: 'Largest available size',
    copy: 'The largest available tribute print with photo, name, dates and memorial link.',
    recommended: false,
    largest: true,
    isMainFulfilment: true,
    frameClassPortrait: 'aspect-[210/297]',
    frameClassLandscape: 'aspect-[297/210]',
  },
];

/** Optional preview-only — not part of main fulfilment path */
export const MEMORIAL_KEEPSAKE_SQUARE_GIFT = {
  id: 'square_gift_preview',
  productType: 'square_gift_preview',
  variantOf: MEMORIAL_PHOTO_LINK_NAME,
  title: 'Square gift preview',
  size: '6 × 6 in',
  sizeDetail: 'Optional square gift preview',
  copy: 'Square gift preview only — main ordering uses A6, A5 or A4.',
  recommended: false,
  largest: false,
  isMainFulfilment: false,
  frameClassPortrait: 'aspect-square',
  frameClassLandscape: 'aspect-square',
};

/** All products for hub cards (main + optional square) */
export const MEMORIAL_KEEPSAKE_PRODUCTS = [
  ...MEMORIAL_KEEPSAKE_MAIN_PRODUCTS,
  MEMORIAL_KEEPSAKE_SQUARE_GIFT,
];
